import type {
  BaseStorage,
  BaseStorageOptions,
} from '@wix/fe-essentials/data-capsule';
import { Scope } from '@wix/data-capsule/dist/types/base-storage';
import merge from 'lodash/merge';

export interface AccountPreferencesMergeStrategyParams {
  readonly storage: BaseStorage<BaseStorageOptions>;
}

export interface AppSettingsViewsSchema {
  views?: {
    list?: Record<string, {} | null | undefined>;
  };
}

export class AccountPreferencesMergeStrategy
  implements BaseStorage<BaseStorageOptions>
{
  private readonly storage;

  constructor(params: AccountPreferencesMergeStrategyParams) {
    this.storage = params.storage;
  }

  extendScope(scope: Scope) {
    return scope;
  }

  async setItem<T>(
    key: string,
    value: T,
    options: BaseStorageOptions,
  ): Promise<void> {
    const { storage } = this;
    const data = (await this.getItem(key, options).catch(() => undefined)) as
      | AppSettingsViewsSchema
      | null
      | undefined;
    const partialData = value as AppSettingsViewsSchema | null | undefined;

    if (data?.views?.list && partialData?.views?.list) {
      for (const [key, value] of Object.entries(partialData.views.list)) {
        if (value === null) {
          // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
          delete data.views.list[key];
          // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
          delete partialData.views.list[key];
        }
      }
    }

    return storage.setItem(key, merge(data, value), options);
  }

  async removeItem(key: string, options: BaseStorageOptions) {
    const { storage } = this;
    const data = await this.getItem(key, options).catch(() => undefined);
    return storage.setItem(key, merge(data, { [key]: null }), options);
  }

  async getItem<T = any>(key: string, options: BaseStorageOptions): Promise<T> {
    return this.storage.getItem(key, options);
  }

  async getAllItems<T = any>(options: BaseStorageOptions): Promise<T> {
    return this.storage.getAllItems(options);
  }
}
